import React, { useEffect, useState } from "react";
import SummaryApi from "../common";
import EventModal from "./EventModal";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AllEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [eventsPerPage] = useState(6);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch(SummaryApi.adminevents.url, {
          method: SummaryApi.adminevents.method,
        });
        const data = await response.json();
        console.log("data", data);
        if (data.status) {
          setEvents(data.data);
        } else {
          setError("Failed to fetch events.");
        }
      } catch (err) {
        setError("Error fetching events.");
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const handleEditClick = (event) => {
    setEditingEvent(event);
    setIsModalOpen(true);
  };

  const handleCreateClick = () => {
    setEditingEvent({
      event_name: "",
      category_name: "",
      event_title: "",
      event_description: "",
      event_date: "",
      event_image: null,
      is_active: "1", // default to "1" for active, but can change later
    });
    setIsModalOpen(true);
  };

  const handleSaveEvent = async (event) => {
    const formData = new FormData();
    console.log("Submitting payload:", event); // Log the payload before submission to check is_active
    if (event.event_id) {
      formData.append("event_id", event.event_id);
    }
    formData.append("event_name", event.event_name);
    formData.append("category_name", event.category_name);
    formData.append("event_title", event.event_title);
    formData.append("event_description", event.event_description);
    formData.append("event_date", event.event_date);
    if (event.new_event_image) {
      formData.append("event_image", event.new_event_image);
    } else if (event.event_image) {
      formData.append("event_image", event.event_image);
    }
    formData.append("is_active", event.is_active ? "1" : "0"); // Ensure is_active is passed correctly

    const url = event.event_id
      ? SummaryApi.updateEvent.url
      : SummaryApi.createEvent.url;
    const method = event.event_id
      ? SummaryApi.updateEvent.method
      : SummaryApi.createEvent.method;

    try {
      const response = await fetch(url, {
        method: method,
        body: formData,
      });
      const data = await response.json();
      console.log("Response data:", data);
      if (data.status) {
        setEvents((prevEvents) => {
          if (event.event_id) {
            return prevEvents.map((ev) =>
              ev.event_id === event.event_id
                ? { ...event, event_id: ev.event_id }
                : ev
            );
          } else {
            return [...prevEvents, { ...event, event_id: data.event_id }];
          }
        });
        toast.success(
          event.event_id
            ? "Event updated successfully!"
            : "Event created successfully!"
        );
        console.log("Updated event data:", event); // Log the updated event
      } else {
        setError("Failed to save event.");
        toast.error("Failed to save event.");
      }
    } catch (err) {
      setError("Error saving event.");
      toast.error("Error saving event.");
    } finally {
      handleCloseModal();
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingEvent(null);
  };

  const paginateEvents = () => {
    const indexOfLastEvent = currentPage * eventsPerPage;
    const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
    return events.slice(indexOfFirstEvent, indexOfLastEvent);
  };

  const totalPages = Math.ceil(events.length / eventsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (loading) {
    return <div className="text-center text-xl font-semibold">Loading...</div>;
  }

  if (error) {
    return (
      <div className="text-center text-xl font-semibold text-red-500">
        {error}
      </div>
    );
  }

  return (
    <div className="mx-auto p-6 max-w-8xl">
      <ToastContainer />
      <h2 className="text-3xl font-bold text-center mb-8">All Events</h2>
      <div className="flex justify-end mb-4">
        <button
          onClick={handleCreateClick}
          className="p-4 bg-blue-500 text-white rounded-md mb-5"
        >
          Create Event
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {paginateEvents().map((event) => {
          const imageUrl = event.event_image
            ? event.event_image.startsWith("images/")
              ? `${SummaryApi.images.url}/${event.event_image.replace("images/", "")}`
              : `${SummaryApi.images.url}/${event.event_image}`
            : "default_image_url";
          const isActive = event.is_active === "1";
          return (
            <div key={event.event_id} className="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col">
            <img
              src={imageUrl}
              alt={event.event_title}
              className="w-full h-64 object-cover"
            />
            <div className="p-6 flex flex-grow flex-col">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                {event.event_name} - {event.category_name}
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                {event.event_title}
              </p>
              <p className="text-gray-600 text-sm mb-4 flex-grow">
                {event.event_description}
              </p>
              <p className="text-gray-500 text-sm mb-4">
                Date: {new Date(event.event_date).toLocaleString()}
              </p>
          
              {/* Status and Edit Button - Always at the bottom */}
              <div className="mt-4 flex justify-between items-center">
                <span
                  className={`inline-block text-sm font-semibold px-3 py-1 rounded-md ${
                    isActive
                      ? "bg-green-100 text-green-600"
                      : "bg-red-100 text-red-600"
                  }`}
                >
                  {isActive ? "Active" : "Inactive"}
                </span>
                <button
                  onClick={() => handleEditClick(event)}
                  className="px-4 py-1 bg-orange-500 text-white rounded-md"
                >
                  Edit
                </button>
              </div>
            </div>
          </div>
          
          );
        })}
      </div>

      {/* Pagination Controls */}
      <div className="flex justify-center mt-6">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-4 py-1 bg-blue-500 text-white rounded-md disabled:bg-gray-400"
        >
          Prev
        </button>
        <span className="mx-4">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-4 py-1 bg-blue-500 text-white rounded-md disabled:bg-gray-400"
        >
          Next
        </button>
      </div>

      {isModalOpen && (
        <EventModal
          event={editingEvent}
          onClose={handleCloseModal}
          onSave={handleSaveEvent}
        />
      )}
    </div>
  );
};

export default AllEvents;
